import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { useForecast, useWarehouses, useMultiplicity } from '../api/hooks';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { BarChartBig, Search, Building2, Boxes, Download } from 'lucide-react';

function ForecastsPage() {
  const { data: forecastData = [], isLoading: loadingForecast } = useForecast();
  const { data: warehouses = [], isLoading: loadingWarehouses } = useWarehouses();
  const { data: multiplicityData = {}, isLoading: loadingMultiplicity } = useMultiplicity();

  const [search, setSearch] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('forecast_value');
  const [viewMode, setViewMode] = useState('table');
  const [groupMode, setGroupMode] = useState('none'); // 'none', 'week', 'month'
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedChartWarehouse, setSelectedChartWarehouse] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const loading = loadingForecast || loadingWarehouses || loadingMultiplicity;

  // Функція округлення до кратності
  const roundToMultiplicity = (value, productId) => {
    const multiplicity = multiplicityData[productId];
    if (!multiplicity || multiplicity <= 1) {
      return Math.ceil(value); // Якщо немає кратності - просто округлюємо вгору
    }
    // Округлюємо до БІЛЬШОГО кратного числа
    return Math.ceil(value / multiplicity) * multiplicity;
  };

  const warehouseMap = useMemo(() => {
    return warehouses.reduce((map, w) => {
      map[w.storehouse_id] = w.name;
      return map;
    }, {});
  }, [warehouses]);

  const categoryOptions = useMemo(() => {
    const categories = new Set(forecastData.map(e => e.category));
    return Array.from(categories);
  }, [forecastData]);

  // Функція для отримання діапазону дат тижня
  const getWeekDateRange = (dateStr) => {
    const date = new Date(dateStr);
    // Знаходимо понеділок (початок тижня)
    const dayOfWeek = date.getDay(); // 0 = неділя, 1 = понеділок, ...
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + daysToMonday);
    
    // Знаходимо неділю (кінець тижня)
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    // Форматуємо дати
    const formatDate = (d) => {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}`;
    };
    
    return `${formatDate(monday)}-${formatDate(sunday)}`;
  };

  const filteredData = useMemo(() => {
    let filtered = forecastData.map(item => ({
      ...item,
      warehouseName: warehouseMap[item.storehouse_id] || item.storehouse_id
    }));

    filtered = filtered.filter(item => {
      const matchProduct = item.product_name.toLowerCase().includes(search.toLowerCase()) || 
        item.product_id.toString().includes(search.toLowerCase());
      const matchWarehouse = !selectedWarehouse || item.warehouseName === selectedWarehouse;
      const matchCategory = !selectedCategory || item.category === selectedCategory;
      return matchProduct && matchWarehouse && matchCategory;
    });

    // Групування по тижнях або місяцях
    if (groupMode !== 'none') {
      const grouped = {};
      filtered.forEach(item => {
        let groupKey;
        let groupLabel;
        
        if (groupMode === 'week') {
          // Групування по тижнях (DD.MM-DD.MM)
          groupLabel = getWeekDateRange(item.forecast_date);
          groupKey = `${item.product_id}_${groupLabel}_${item.storehouse_id}`;
        } else if (groupMode === 'month') {
          // Групування по місяцях (YYYY-MM)
          groupLabel = item.forecast_date.substring(0, 7); // "2025-10-01" -> "2025-10"
          groupKey = `${item.product_id}_${groupLabel}_${item.storehouse_id}`;
        }
        
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            ...item,
            forecast_date: groupLabel,
            forecast_value: 0,
            days_count: 0,
            original_value: 0
          };
        }
        grouped[groupKey].forecast_value += item.forecast_value;
        grouped[groupKey].days_count += 1;
      });
      
      // Округлюємо до кратності після групування
      filtered = Object.values(grouped).map(item => {
        const originalValue = item.forecast_value;
        const roundedValue = roundToMultiplicity(item.forecast_value, item.product_id);
        return {
          ...item,
          original_value: originalValue,
          forecast_value: roundedValue
        };
      });
    }

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [forecastData, warehouseMap, search, selectedWarehouse, selectedCategory, sortField, sortOrder, groupMode]);

  const csvUrl = useMemo(() => {
    if (filteredData.length === 0) return null;
    const csv = Papa.unparse(filteredData);
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }, [filteredData]);

  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  const paginatedData = filteredData.slice(start, end);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const productOptions = useMemo(() => {
    return [...new Set(filteredData.map(e => `${e.product_id} - ${e.product_name}`))];
  }, [filteredData]);

  const chartData = useMemo(() => {
    const [idStr] = selectedProduct.split(' - ');
    const id = parseInt(idStr);
    const productForecast = filteredData.filter(h =>
      h.product_id === id && h.warehouseName === selectedChartWarehouse
    );
    const sorted = productForecast.sort((a, b) => new Date(a.forecast_date) - new Date(b.forecast_date));
    return {
      labels: sorted.map(d => d.forecast_date),
      datasets: [{
        label: selectedProduct,
        data: sorted.map(d => d.forecast_value),
        borderColor: 'blue',
        tension: 0.1
      }]
    };
  }, [filteredData, selectedProduct, selectedChartWarehouse]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">Прогноз по складам (14 днів)</h2>

      {/* Filter Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="flex items-center border rounded px-2">
          <Search className="w-4 h-4 text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Пошук за ID або назвою..."
            className="py-2 outline-none w-full"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="flex items-center border rounded px-2">
          <Building2 className="w-4 h-4 text-gray-500 mr-2" />
          <select
            className="py-2 outline-none w-full"
            value={selectedWarehouse}
            onChange={e => setSelectedWarehouse(e.target.value)}
          >
            <option value="">Усі склади</option>
            {warehouses.map(w => (
              <option key={w.storehouse_id} value={w.name}>{w.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center border rounded px-2">
          <Boxes className="w-4 h-4 text-gray-500 mr-2" />
          <select
            className="py-2 outline-none w-full"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="">Усі категорії</option>
            {categoryOptions.sort().map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex gap-2">
          <select
            value={viewMode}
            onChange={e => setViewMode(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="table">Таблиця</option>
            <option value="chart">Графік</option>
          </select>
          
          {viewMode === 'table' && (
            <select
              value={groupMode}
              onChange={e => setGroupMode(e.target.value)}
              className="border p-2 rounded bg-white"
            >
              <option value="none">📋 Деталізовано (по днях)</option>
              <option value="week">📅 По тижнях</option>
              <option value="month">📆 По місяцях</option>
            </select>
          )}
        </div>
        {csvUrl && (
          <a href={csvUrl} download="forecast.csv" className="flex items-center bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            <Download className="w-4 h-4 mr-2" />
            Завантажити CSV
          </a>
        )}
      </div>

      {/* Chart Options */}
      {viewMode === 'chart' && (
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <div className="flex items-center border rounded px-2">
            <BarChartBig className="w-4 h-4 text-gray-500 mr-2" />
            <input
              list="products"
              className="py-2 outline-none w-64"
              placeholder="Введіть назву або ID продукту..."
              value={selectedProduct}
              onChange={e => {
                setSelectedProduct(e.target.value);
                setShowChart(false);
              }}
            />
            <datalist id="products">
              {productOptions.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>

          <div className="flex items-center border rounded px-2">
            <Building2 className="w-4 h-4 text-gray-500 mr-2" />
            <select
              className="py-2 outline-none w-48"
              value={selectedChartWarehouse}
              onChange={e => {
                setSelectedChartWarehouse(e.target.value);
                setShowChart(false);
              }}
            >
              <option value="">Оберіть склад</option>
              {warehouses.map(w => (
                <option key={w.storehouse_id} value={w.name}>{w.name}</option>
              ))}
            </select>
          </div>

          <button
            disabled={!selectedProduct || !selectedChartWarehouse}
            onClick={() => setShowChart(true)}
            className={`px-4 py-2 rounded text-white ${selectedProduct && selectedChartWarehouse ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'}`}
          >
            Показати графік
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4 animate-pulse">
          <div className="bg-blue-600 h-2.5 rounded-full w-1/2 animate-pulse"></div>
        </div>
      ) : viewMode === 'chart' && showChart ? (
        <div className="bg-white p-6 shadow rounded">
          {chartData.labels.length > 0 ? (
            <Line data={chartData} options={{
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                title: { display: true, text: selectedProduct }
              },
              scales: {
                y: { title: { display: true, text: 'Прогнозована кількість' } },
                x: { title: { display: true, text: 'Дата' } }
              }
            }} />
          ) : (
            <p className="text-center text-gray-500">Немає даних для цього поєднання</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed bg-white border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th style={{ width: '80px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('product_id'); toggleSort(); }}>ID ↑↓</th>
                <th style={{ width: '240px' }} className="py-2 px-4 border cursor-pointer" onClick={() => { setSortField('product_name'); toggleSort(); }}>Продукт ↑↓</th>
                <th style={{ width: '160px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('category'); toggleSort(); }}>Категорія ↑↓</th>
                <th style={{ width: '100px' }} className="py-2 px-2 border cursor-pointer text-center" onClick={() => { setSortField('forecast_value'); toggleSort(); }}>Прогноз ↑↓</th>
                <th style={{ width: '120px' }} className="py-2 px-2 border cursor-pointer text-center" onClick={() => { setSortField('forecast_date'); toggleSort(); }}>
                  {groupMode === 'week' ? 'Тиждень ↑↓' : groupMode === 'month' ? 'Місяць ↑↓' : 'Дата ↑↓'}
                </th>
                <th style={{ width: '110px' }} className="py-2 px-2 border cursor-pointer text-center" onClick={() => { setSortField('accuracy'); toggleSort(); }}>
                  Вірогідність, % ↑↓
                </th>
                {groupMode !== 'none' && (
                  <th style={{ width: '80px' }} className="py-2 px-2 border cursor-pointer text-center" onClick={() => { setSortField('days_count'); toggleSort(); }}>Днів ↑↓</th>
                )}
                <th style={{ width: '180px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('warehouseName'); toggleSort(); }}>Склад ↑↓</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx) => (
                <tr key={idx} className={item.insufficient_data ? 'bg-gray-50' : ''}>
                  <td className="py-1 px-2 border text-center">{item.product_id}</td>
                  <td className="py-1 px-2 border truncate">{item.product_name}</td>
                  <td className="py-1 px-2 border">{item.category}</td>
                  <td className="py-1 px-2 border text-center">
                    {item.insufficient_data ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : (
                      groupMode !== 'none' ? Math.round(item.forecast_value) : item.forecast_value
                    )}
                  </td>
                  <td className="py-1 px-2 border text-center">
                    {item.insufficient_data ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : (
                      item.forecast_date
                    )}
                  </td>
                  <td className="py-1 px-2 border text-center">
                    {item.insufficient_data ? (
                      <span className="px-2 py-1 rounded text-xs bg-gray-200 text-gray-600" title={item.insufficient_reason || 'Недостатньо даних'}>
                        ⚠️ Мало даних
                      </span>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        (item.accuracy || 0) >= 80 ? 'bg-green-100 text-green-800' :
                        (item.accuracy || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.accuracy || 0}%
                      </span>
                    )}
                  </td>
                  {groupMode !== 'none' && (
                    <td className="py-1 px-2 border text-center">
                      {item.insufficient_data ? (
                        <span className="text-gray-400 text-xs">—</span>
                      ) : (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {item.days_count}
                      </span>
                      )}
                    </td>
                  )}
                  <td className="py-1 px-2 border truncate">{item.warehouseName}</td>
                </tr>
              ))}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={groupMode !== 'none' ? "8" : "7"} className="py-4 text-center text-gray-500">Прогнозів не знайдено</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 gap-4">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
          >
            ← Попередня
          </button>

          <div className="flex items-center gap-2">
            <span>Сторінка</span>
            <input
              type="number"
              value={currentPage}
              min={1}
              max={totalPages}
              onChange={(e) => {
                const page = Math.min(totalPages, Math.max(1, parseInt(e.target.value) || 1));
                setCurrentPage(page);
              }}
              className="border rounded px-2 py-1 w-16 text-center"
            />
            <span>з {totalPages}</span>
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
          >
            Наступна →
          </button>
        </div>
        </div>
      )}
    </div>
  );
}

export default ForecastsPage;
